/**
 * Infinite image carousel — driven by requestAnimationFrame instead of a
 * CSS @keyframes loop.
 *
 * Why: a CSS-only marquee needs two Liquid-rendered halves to be pixel-
 * identical for the loop to be invisible. In practice that breaks down —
 * images load at different times, some themes override native lazy
 * loading, and any mismatch shows up as a visible "jump" or a blank/
 * broken slide. This version renders the slides ONCE in Liquid, clones
 * them here after they're in the DOM, and measures real pixel widths at
 * runtime — so the loop point is always exact, and nothing is skipped.
 *
 * There is also no hover handling anywhere in this file, on purpose —
 * the animation never pauses or reacts to interaction.
 */

function setupCarousel(track) {
  const wrapper = track.closest('.carousel-track-container');
  if (!wrapper) return;

  const speed = parseFloat(track.dataset.speed) || 60; // pixels per second
  const originalSlides = Array.from(track.children);
  if (originalSlides.length === 0) return;

  let repeatDistance = 0;
  let position = 0;
  let lastTimestamp = null;
  let rafId = null;

  function cloneOriginalSet() {
    const fragment = document.createDocumentFragment();
    originalSlides.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      // Clones don't need to be announced by screen readers
      const img = clone.querySelector('img');
      if (img) img.setAttribute('alt', '');
      fragment.appendChild(clone);
    });
    track.appendChild(fragment);
  }

  function measure() {
    // The width/height of each slide is fixed by CSS (--slide-width +
    // aspect-ratio), so this is accurate immediately — no need to wait
    // for images to finish loading.
    const firstSlideLeft = track.children[0].getBoundingClientRect().left;
    const firstCloneLeft = originalSlides.length < track.children.length
      ? track.children[originalSlides.length].getBoundingClientRect().left
      : 0;
    repeatDistance = firstCloneLeft - firstSlideLeft;
  }

  function ensureEnoughContent() {
    // Always keep at least one full extra set beyond the wrapper's
    // visible width, so the loop never visibly runs out of slides,
    // even on very wide screens.
    let guard = 0; // safety cap in case measurement ever comes back 0
    while (
      track.scrollWidth < wrapper.clientWidth + repeatDistance + 1 &&
      guard < 20
    ) {
      cloneOriginalSet();
      measure();
      guard += 1;
    }
  }

  function tick(timestamp) {
    if (lastTimestamp === null) lastTimestamp = timestamp;
    const deltaSeconds = (timestamp - lastTimestamp) / 1000;
    lastTimestamp = timestamp;

    position -= speed * deltaSeconds;
    if (repeatDistance > 0 && position <= -repeatDistance) {
      position += repeatDistance;
    }

    track.style.transform = 'translate3d(' + position + 'px, 0, 0)';
    rafId = requestAnimationFrame(tick);
  }

  function start() {
    cloneOriginalSet();
    measure();
    ensureEnoughContent();
    if (rafId) cancelAnimationFrame(rafId);
    lastTimestamp = null;
    rafId = requestAnimationFrame(tick);
  }

  start();

  // Re-measure on resize so the loop stays exact if layout changes
  // (e.g. rotating a device, or the slide-width setting responding to
  // a breakpoint).
  let resizeTimeout;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      measure();
      ensureEnoughContent();
    }, 150);
  });
}

function initAllCarousels() {
  document.querySelectorAll('[data-carousel-track]').forEach((track) => {
    // Guard against double-init if a section re-renders (e.g. in the
    // theme editor).
    if (track.dataset.carouselInitialized) return;
    track.dataset.carouselInitialized = 'true';
    setupCarousel(track);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllCarousels);
} else {
  initAllCarousels();
}

// Re-init when a section is added/re-rendered in the theme editor
document.addEventListener('shopify:section:load', function (event) {
  const track = event.target.querySelector('[data-carousel-track]');
  if (track) setupCarousel(track);
});